import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, RadioGroup, FormControlLabel, Select, MenuItem, CircularProgress, Box, Button, TextField
} from '@mui/material';
import api from '../../services/api';
import {
  Add,
  Remove,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDateDisplay } from '../../utils/dateUtils';

const DespatchForm = () => {
  const [rows, setRows] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wastagePercentages, setWastagePercentages] = useState({});
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch orders
      const ordersRes = await api.get('/api/shop/orders');
      // Flatten items, but only include those not yet despatched
      const allItems = ordersRes.data.flatMap(order => order.items || []);
      console.log('Fetched items:', allItems.map(item => ({ _id: item._id, despatchStatus: item.despatchStatus, itemName: item.itemName })));
      const itemRows = ordersRes.data.flatMap(order =>
        (order.items || [])
          .filter(item => !item.despatchStatus)
          .map(item => ({
            billName: order.billName,
            billNumber: order.billNumber,
            orderDate: formatDateDisplay(order.orderDate),
            deliveryDate: formatDateDisplay(order.deliveryDate),
            requiredDate: formatDateDisplay(order.requiredDate),
            orPriority: order.isUrgent ? 'Urgent' : 'Normal',
            salesman: order.agentName,
            itemName: item.itemName,
            itemQty: item.quantity,
            quantityUnit: item.unit,
            itemWeight: item.itemWeight,
            karat: item.karat,
            despatch: false,
            karigar: '',
            wastage: item.wastage || '',
            orderId: order._id,
            itemId: item._id
          }))
      );
      setRows(itemRows);
      // Fetch karigars
      const karigarsRes = await api.get('/api/karigar');
      setKarigars(karigarsRes.data);
    } catch (err) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDespatchChange = (index, value) => {
    setRows(rows => rows.map((row, i) => i === index ? { ...row, despatch: value === 'yes' } : row));
  };

  const handleKarigarChange = (index, value) => {
    setRows(rows => rows.map((row, i) => i === index ? { ...row, karigar: value } : row));
  };

  const handleWastageChange = (index, value) => {
    setRows(rows => rows.map((row, i) => i === index ? { ...row, wastage: value } : row));
  };

  const handleDespatch = async (row, idx) => {
    try {
      // Fetch the full order
      const orderRes = await api.get(`/api/shop/orders/${row.orderId}`);
      const order = orderRes.data;
      // Update the specific item
      const updatedItems = order.items.map(item =>
        item._id === row.itemId
          ? { ...item, despatchStatus: true, karigarAbbreviation: row.karigar, wastage: row.wastage }
          : item
      );
      await api.put(`/api/shop/orders/${row.orderId}`, { ...order, items: updatedItems });
      // Re-fetch data so the despatched item is removed
      await fetchData();
    } catch (err) {
      setError('Failed to update despatch status');
    }
  };

  const handleUndoDespatch = async (row) => {
    try {
      // Fetch the full order
      const orderRes = await api.get(`/api/shop/orders/${row.orderId}`);
      const order = orderRes.data;
      // Update the specific item
      const updatedItems = order.items.map(item =>
        item._id === row.itemId
          ? { ...item, despatchStatus: false }
          : item
      );
      await api.put(`/api/shop/orders/${row.orderId}`, { ...order, items: updatedItems });
      // Re-fetch data so the item reappears
      await fetchData();
    } catch (err) {
      setError('Failed to undo despatch status');
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  }

  if (error) {
    return <Container maxWidth="xl" sx={{ py: 6 }}><Paper sx={{ p: 4 }}><Typography color="error">{error}</Typography></Paper></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" color="primary" sx={{ mb: 2 }}>
          Back
        </Button>
        
        <Typography variant="h4" gutterBottom color="primary">
          Karigar Despatch
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Bill</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Order Date</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Delivery</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Required</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Priority</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Salesman</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Item</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Qty</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Wt(g)</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Karat</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Despatch</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Status</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Karigar</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>Wastage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                return (
                  <TableRow key={row.billNumber + '-' + row.itemName + '-' + idx}>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.billName} {row.billNumber}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.orderDate}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.deliveryDate}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.requiredDate}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.orPriority}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.salesman}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.itemName}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.itemQty} {row.quantityUnit}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.itemWeight}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.karat}</TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <RadioGroup
                        row
                        value={row.despatch ? 'yes' : 'no'}
                        onChange={e => handleDespatchChange(idx, e.target.value)}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
                      >
                        <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>
                      {row.despatch ? <Typography variant="caption" color="success.main">Despatched</Typography> : ''}
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={row.karigar}
                        onChange={e => handleKarigarChange(idx, e.target.value)}
                        displayEmpty
                        renderValue={selected => selected || 'Select Karigar'}
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="">
                          <em>Select Karigar</em>
                        </MenuItem>
                        {karigars.map(k => (
                          <MenuItem key={k.abbreviation} value={k.abbreviation}>
                            {k.abbreviation} - {k.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="Wastage"
                        name="wastage"
                        value={wastagePercentages[row.billNumber + '-' + row.itemName] || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setWastagePercentages({
                            ...wastagePercentages,
                            [row.billNumber + '-' + row.itemName]: value
                          });
                          setRows(rows => rows.map((r, i) => i === idx ? { ...r, wastage: value } : r));
                        }}
                        size="small"
                        fullWidth
                        disabled={!row.despatch}
                      />
                    </TableCell>
                    <TableCell>
                      {row.despatch && !row.despatchStatus && row.karigar && row.wastage ? (
                        <Button variant="contained" size="small" onClick={() => handleDespatch(row, idx)}>
                          Despatch
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default DespatchForm;